import React, { useState } from 'react';

import { createExtendedResponseTape } from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import { DbTable, Util, Receivable } from '@finley-technologies/finley-types';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import {
  getCreditFacilityBySlug,
  getLoanProductSlug,
  useGraph,
} from '../GraphContext';
import { ProgressWithDownloadButton } from './ProgressWithDownloadButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

type Props = {
  loanProductMechanics: CreditAgreement.Config.ResolvedLoanProductMechanics;
  facilityType: DbTable.FacilityType;
  borrowingBaseReceivables: Receivable.CreditFacility.BorrowingBaseReceivable[];
  tapeBuilderNode: TapeBuilderUI.types.OperationNodes.CalcAgent.UINode;
  // eslint-disable-next-line react/no-unused-prop-types
  reportingDate: Util.FullDateStr;
  multiLoanProductConcentrationLimits: CreditAgreement.Config.ResolvedMultiLoanProductConcentrationLimit[];
  onDone: (output: Util.SanitizedCsv) => void;
};
export const ExtendedResponseTapeEvaluator = ({
  loanProductMechanics,
  facilityType,
  borrowingBaseReceivables,
  tapeBuilderNode,
  multiLoanProductConcentrationLimits,
  onDone,
}: Props) => {
  const graphContext = useGraph();
  const loanProductSlug = getLoanProductSlug(
    graphContext,
    loanProductMechanics.loanProductId,
  );
  finleyAssert(
    loanProductSlug,
    `loanProductSlug not found for ${loanProductMechanics.loanProductId}`,
  );

  finleyAssert(
    TapeBuilderUI.operationNodes.operation_calcAgent.isNode(tapeBuilderNode),
    'node is not a calcAgent node',
  );
  const creditFacilityId = getCreditFacilityBySlug(
    graphContext,
    tapeBuilderNode.configuration.creditFacilitySlug,
  );
  finleyAssert(creditFacilityId, 'creditFacilityId not found');

  const [progressPercent, setProgressPercent] = useState(0);
  const [progressText, setProgressText] = useState('');

  const generateExtendedResponseTape = async () => {
    finleyAssert(
      borrowingBaseReceivables.length > 0,
      'No receivables found. Did you forget to run calc-agent?',
    );

    const { extendedResponseTape } = await createExtendedResponseTape({
      facilityType,
      loanProductMechanics,
      borrowingBaseReceivables,
      multiLoanProductConcentrationLimits,
      progressCallback: async (
        operation: string,
        strProgress: string,
        percent: number,
      ) => {
        setProgressText(`${operation}: ${strProgress}`);
        setProgressPercent(percent);
        await new Promise((resolve) => setTimeout(resolve, 0));
      },
    });

    setProgressPercent(100);
    setProgressText('Done!');
    onDone(extendedResponseTape);
    return extendedResponseTape;
  };

  const filename = `${loanProductSlug}_extended_response_tape.csv`;
  return (
    <ProgressWithDownloadButton
      onStart={generateExtendedResponseTape}
      buttonText={`Extended Response Tape: ${loanProductSlug}`}
      progressPercent={progressPercent}
      progressText={progressText}
      filename={filename}
    />
  );
};
